import { CircleAlert, CircleCheck, UserX } from 'lucide-react';
import type { GridSummary } from '@/store/api';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  ═════════════════════════════════════════════════════════════════════════════
  IS THIS WEEK FINISHED, AND IS IT RIGHT?

  Two questions a grid of boxes cannot answer, however well it is drawn.

  "Are there holes in it?"
  ────────────────────────
  Nobody reads 35 cells and comes away knowing whether four are empty or
  fourteen. Counting them is the difference between a picture of a week and a
  check on one — and it is what a head teacher actually wants on the first day
  of term.

  "Does Class 3 get enough Maths?"
  ────────────────────────────────
  The curriculum is expressed in periods per week, and that number is only
  obtainable from this grid by counting cells by eye across five columns. So
  the subject tally is listed, heaviest first, and comparing it against the
  syllabus becomes reading rather than arithmetic.

  Why `unstaffed` is called out separately from `empty`
  ────────────────────────────────────────────────────
  They are different problems with different owners. An empty slot is
  unfinished timetabling; a lesson with no teacher is unfinished STAFFING, it
  is perfectly normal in August and a crisis in September, and it is invisible
  on the grid because the cell looks filled.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function TimetableSummary({ summary }: { summary: GridSummary }) {
  const { teachingSlots, filled, empty, unstaffed, bySubject } = summary;
  const done = teachingSlots > 0 && empty === 0;
  const pct = teachingSlots === 0 ? 0 : Math.round((filled / teachingSlots) * 100);

  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-border bg-card px-3.5 py-3">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <span className="flex items-center gap-1.5">
          {done ? (
            <CircleCheck size={13} className="text-[hsl(var(--record-ink-success))]" />
          ) : (
            <CircleAlert size={13} className="text-muted-foreground" />
          )}
          <span className="text-[11.5px] font-bold tabular-nums text-foreground">
            {filled} of {teachingSlots}
          </span>
          <span className={styles.meta}>periods timetabled</span>
        </span>

        {empty > 0 && (
          <span className={cn(styles.meta, 'tabular-nums')}>
            {empty} still empty
          </span>
        )}

        {unstaffed > 0 && (
          <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[hsl(var(--record-ink-warning))]">
            <UserX size={12} />
            <span className="tabular-nums">{unstaffed}</span>
            lesson{unstaffed === 1 ? '' : 's'} with no teacher
          </span>
        )}

        {done && unstaffed === 0 && (
          <span className="text-[11px] font-semibold text-[hsl(var(--record-ink-success))]">
            Week complete and fully staffed.
          </span>
        )}

        {/* A bar rather than only a number: "23 of 35" takes a moment to turn
            into "two thirds", and a wall-glance should not need arithmetic. */}
        <span className="ml-auto flex items-center gap-2">
          <span className="h-1.5 w-28 overflow-hidden rounded-full bg-muted">
            <span
              className={cn(
                'block h-full rounded-full transition-all',
                done ? 'bg-success' : 'bg-primary',
              )}
              style={{ width: `${pct}%` }}
            />
          </span>
          <span className={cn(styles.meta, 'tabular-nums')}>{pct}%</span>
        </span>
      </div>

      {bySubject.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-border pt-2.5">
          <span className={cn(styles.label, 'mr-0.5')}>Per week</span>
          {bySubject.map((s) => (
            <span
              key={s.subjectId}
              className="rounded border border-border bg-muted/50 px-1.5 py-0.5 text-[10.5px] font-medium text-foreground"
            >
              {s.subject}
              <span className="ml-1 font-bold tabular-nums text-muted-foreground">{s.periods}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
